import React, { useEffect, useState, useRef, useCallback } from "react";
import { AvatarChip } from "./AvatarChip";
import {
  fetchDmThread, sendDm, markDmThreadRead, reactToDm,
  fetchUserTeams, inviteToTeamMailbox, fetchUpcomingGames, createChallenge,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useUserHub } from "../hooks/useUserHub";

const REACTIONS = ["🔥", "⚔", "🤝", "👀", "💀", "😂", "🚀"];

/**
 * Format a DM timestamp for the chat bubble. Shows:
 *  - "Just now" for the last 60 s
 *  - "3 min ago" / "1 h ago" for the last 24 h
 *  - "Yesterday 14:32" / "Mon 14:32" for the last 7 days
 *  - "Mar 12, 14:32" for older messages
 * The full ISO timestamp is exposed via the `title` attribute on the
 * bubble so power users can hover to see the precise time.
 */
function formatDmTimestamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const diffSec = Math.floor((now - d) / 1000);
  if (diffSec < 60) return "Just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  const sameDay = d.toDateString() === now.toDateString();
  const hhmm = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `${Math.floor(diffSec / 3600)} h ago · ${hhmm}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${hhmm}`;
  if (diffSec < 7 * 24 * 3600) {
    const weekday = d.toLocaleDateString([], { weekday: "short" });
    return `${weekday} ${hhmm}`;
  }
  const dateStr = d.toLocaleDateString([], { month: "short", day: "numeric" });
  return `${dateStr}, ${hhmm}`;
}

export function DmModal({ targetUser, onClose, onToast }) {
  const { user, refreshInboxCount } = useAuth();
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(true);
  const [policy, setPolicy] = useState("everyone");
  const [areFriends, setAreFriends] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [reactingFor, setReactingFor] = useState("");
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [myTeams, setMyTeams] = useState([]);
  const [targetTeams, setTargetTeams] = useState([]);
  const bottomRef = useRef(null);
  // Tick state used to keep relative timestamps fresh ("Just now" → "1 min ago")
  // without re-fetching the thread.
  const [, setNowTick] = useState(0);
  // Guard against overlapping fetches so a slow response can't clobber a
  // fresher one — and the optimistic local-append doesn't get overwritten.
  const inFlightRef = useRef(false);
  const target = targetUser;

  /**
   * De-duplicate by `message_id` while keeping chronological order.
   * Locally-appended messages from `sendDm()` or `dm:new` WS events stay
   * even if a slow poll response doesn't yet include them — they get
   * merged in on the next poll once Mongo has caught up.
   */
  const mergeMessages = useCallback((existing, incoming) => {
    const byId = new Map();
    for (const m of existing) byId.set(m.message_id, m);
    for (const m of incoming) byId.set(m.message_id, m);
    return Array.from(byId.values()).sort((a, b) =>
      (a.created_at || "").localeCompare(b.created_at || ""),
    );
  }, []);

  const load = useCallback(() => {
    if (!target?.user_id || inFlightRef.current) return Promise.resolve();
    inFlightRef.current = true;
    return fetchDmThread(target.user_id)
      .then((d) => {
        setMessages((prev) => mergeMessages(prev, d.messages || []));
        setOpen(d.messaging_open !== false);
        setPolicy(d.messaging_policy || "everyone");
        setAreFriends(!!d.are_friends);
      })
      .catch(() => { /* 401 silent */ })
      .finally(() => { inFlightRef.current = false; });
  }, [target?.user_id, mergeMessages]);

  useEffect(() => {
    if (!target?.user_id) return;
    let cancelled = false;
    // Reset state when switching to a new conversation.
    setMessages([]);
    load();
    markDmThreadRead(target.user_id).then(() => refreshInboxCount?.()).catch(() => {});
    // Fallback poll — WebSocket should deliver new messages instantly, but
    // we keep a slow poll so a momentarily-disconnected socket still
    // converges. 15s is generous since the socket carries the real load.
    const pollId = setInterval(() => { if (!cancelled) load(); }, 15000);
    // Refresh relative timestamps every 30s so "Just now" turns into
    // "1 min ago" → "2 min ago" automatically.
    const tickId = setInterval(() => setNowTick((t) => t + 1), 30000);
    return () => { cancelled = true; clearInterval(pollId); clearInterval(tickId); };
  }, [target?.user_id, load, refreshInboxCount]);

  // Real-time inbound: the server fires `dm:new` over the user-hub WS the
  // moment a message lands in our pair_key — both for messages addressed
  // TO us from the target AND for messages I sent from another tab/device
  // (server pushes to sender too so multi-device stays in sync). In
  // either case we refetch the thread (cheap + accurate) and clear the
  // unread badge.
  useUserHub(useCallback((evt) => {
    if (evt?.type !== "dm:new") return;
    if (!target?.user_id || !user?.user_id) return;
    const fromTarget = evt.from_user_id === target.user_id;
    const sentByMeToTarget = evt.from_user_id === user.user_id
      && evt.to_user_id === target.user_id;
    if (!fromTarget && !sentByMeToTarget) return;
    // Trigger a quick refetch — the lightweight WS payload doesn't
    // carry the full message doc (no reactions, avatar, etc.) so
    // pulling the thread is the cleanest way to stay accurate. The
    // `inFlightRef` guard above keeps this from racing the poll.
    load();
    if (fromTarget) {
      markDmThreadRead(target.user_id).then(() => refreshInboxCount?.()).catch(() => {});
    }
  }, [target?.user_id, user?.user_id, load, refreshInboxCount]));

  // Load both users' team rosters so we can drive the INVITE / CHALLENGE
  // shortcuts. Only my teams where I'm a manager can invite; only target's
  // teams that share a league with one of my managed teams can be challenged.
  useEffect(() => {
    if (!user?.user_id || !target?.user_id) return;
    fetchUserTeams(user.user_id).then(setMyTeams).catch(() => setMyTeams([]));
    fetchUserTeams(target.user_id).then(setTargetTeams).catch(() => setTargetTeams([]));
  }, [user?.user_id, target?.user_id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  if (!target) return null;

  const submit = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const m = await sendDm(target.user_id, body);
      // Use merge instead of unconditional append so a fast poll mid-send
      // can't insert a duplicate of the same message_id.
      setMessages((prev) => mergeMessages(prev, [m]));
      setDraft("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send.";
      onToast?.(detail);
      if (err?.response?.status === 403) setOpen(false);
    } finally { setSending(false); }
  };

  const react = async (msg, emoji) => {
    try {
      const r = await reactToDm(msg.message_id, emoji);
      setMessages((prev) => prev.map((m) =>
        m.message_id === msg.message_id ? { ...m, reactions: r.reactions } : m
      ));
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Reaction failed.");
    } finally { setReactingFor(""); }
  };

  // Teams I manage = eligible to invite from.
  const myManagedTeams = myTeams.filter((t) => t.is_manager);
  // Pairs (my managed team, target's team) sharing a league_id = challengeable.
  const challengePairs = [];
  for (const a of myManagedTeams) {
    for (const b of targetTeams) {
      if (a.league_id && a.league_id === b.league_id && a.team_id !== b.team_id) {
        challengePairs.push({ from: a, to: b });
      }
    }
  }

  const sendInvite = async (team) => {
    try {
      const r = await inviteToTeamMailbox(team.team_id, target.user_id);
      onToast?.(r.already_pending
        ? `📬 ${target.name} already has a pending invite to ${team.name}.`
        : `📬 Invite to ${team.name} sent to ${target.name}'s inbox.`);
      setShortcutsOpen(false);
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Invite failed.");
    }
  };

  const sendChallenge = async (pair) => {
    // Default: tomorrow at 19:00 local time. User can edit from the league
    // page after — keeping the in-DM flow zero-friction.
    const when = new Date();
    when.setDate(when.getDate() + 1);
    when.setHours(19, 0, 0, 0);
    try {
      await createChallenge({
        from_team_id: pair.from.team_id,
        to_team_id: pair.to.team_id,
        scheduled_at: when.toISOString(),
        note: `DM challenge from ${pair.from.name}.`,
      });
      onToast?.(`⚡ Challenge sent: ${pair.from.name} ⚔ ${pair.to.name} (tomorrow 7 PM). Edit time on the league page.`);
      setShortcutsOpen(false);
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Challenge failed.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[99] bg-black/85 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="dm-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass rounded-3xl w-full max-w-md h-[80vh] max-h-[100dvh] neon-glow flex flex-col"
        data-testid="dm-modal"
      >
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-3 shrink-0">
          <AvatarChip avatarId={target.avatar_id} pfpUrl={target.pfp_url} size={36} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6]">DIRECT MESSAGE</p>
            <p className="font-bold truncate">{target.name}</p>
          </div>
          {(myManagedTeams.length > 0 || challengePairs.length > 0) && (
            <button
              type="button"
              onClick={() => setShortcutsOpen((v) => !v)}
              data-testid="dm-shortcuts-toggle"
              className={`text-[10px] tracking-widest px-2 py-1 rounded-lg border ${
                shortcutsOpen
                  ? "bg-[#14b8a6]/20 border-[#14b8a6] text-[#14b8a6]"
                  : "bg-black/40 border-white/20 text-white/70 hover:text-white"
              }`}
              title="Convert this DM into a team invite or challenge"
            >
              <i className="fas fa-bolt mr-1" />ACT
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="dm-close-btn"
            className="text-white/60 hover:text-white text-xl"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {shortcutsOpen && (
          <div className="border-b border-[#14b8a6]/30 bg-[#14b8a6]/5 p-3 space-y-2" data-testid="dm-shortcuts-panel">
            {myManagedTeams.length > 0 && (
              <div>
                <p className="text-[9px] tracking-[3px] text-[#14b8a6] mb-1.5">
                  <i className="fas fa-shield-halved mr-1" />INVITE {target.name?.split(" ")[0] || "them"} TO A TEAM
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {myManagedTeams.slice(0, 6).map((t) => (
                    <button
                      key={t.team_id}
                      type="button"
                      onClick={() => sendInvite(t)}
                      data-testid={`dm-invite-${t.team_id}`}
                      className="text-[10px] tracking-wide text-left p-2 rounded-lg bg-black/40 border border-white/15 hover:border-[#14b8a6] hover:bg-[#14b8a6]/10 truncate"
                    >
                      <i className="fas fa-crown text-[#14b8a6] mr-1 text-[9px]" />
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {challengePairs.length > 0 && (
              <div>
                <p className="text-[9px] tracking-[3px] text-[#14b8a6] mb-1.5">
                  <i className="fas fa-bolt mr-1" />CHALLENGE THEIR TEAM
                </p>
                <div className="space-y-1.5">
                  {challengePairs.slice(0, 4).map((pair, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => sendChallenge(pair)}
                      data-testid={`dm-challenge-${idx}`}
                      className="w-full text-[10px] tracking-wide text-left p-2 rounded-lg bg-black/40 border border-[#14b8a6]/30 hover:border-[#14b8a6] hover:bg-[#14b8a6]/10"
                    >
                      <span className="text-white">{pair.from.name}</span>
                      <span className="text-white/40 mx-1">⚔</span>
                      <span className="text-[#14b8a6]">{pair.to.name}</span>
                      <span className="block text-[9px] text-white/40 mt-0.5">Scheduled for tomorrow 7 PM (edit later)</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {myManagedTeams.length === 0 && challengePairs.length === 0 && (
              <p className="text-[10px] text-white/40 text-center py-2">
                Manage a team in a shared league to unlock challenge shortcuts.
              </p>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="dm-thread">
          {messages.length === 0 ? (
            <p className="text-xs text-white/40 text-center py-8">
              No messages yet — break the ice.
            </p>
          ) : messages.map((m) => {
            const isMe = m.from_user_id !== target.user_id;
            const reactions = m.reactions || {};
            const hasReactions = Object.keys(reactions).length > 0;
            return (
              <div
                key={m.message_id}
                data-testid={`dm-msg-${m.message_id}`}
                className={`flex gap-2 ${isMe ? "justify-end" : "justify-start"} group`}
              >
                {!isMe && <AvatarChip avatarId={m.from_user_avatar_id} pfpUrl={m.from_user_pfp_url} size={28} />}
                <div className={`flex flex-col gap-1 max-w-[75%] ${isMe ? "items-end" : "items-start"}`}>
                  <div className="relative">
                    <div
                      className={`rounded-2xl px-3 py-2 text-sm ${
                        isMe
                          ? "bg-[#14b8a6]/15 border border-[#14b8a6]/40 text-white"
                          : "bg-black/40 border border-white/10 text-white"
                      }`}
                      title={m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                    >
                      {m.body}
                    </div>
                    <button
                      type="button"
                      onClick={() => setReactingFor(reactingFor === m.message_id ? "" : m.message_id)}
                      data-testid={`dm-react-toggle-${m.message_id}`}
                      className={`absolute -bottom-2 ${isMe ? "-left-2" : "-right-2"} w-6 h-6 rounded-full bg-black/80 border border-white/20 text-white/60 hover:text-white hover:border-[#14b8a6] flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity`}
                      title="React"
                    >
                      <i className="far fa-smile" />
                    </button>
                  </div>
                  {/* Timestamp under each bubble. Hover the bubble itself
                      to see the full localised datetime via `title`. */}
                  <span
                    data-testid={`dm-msg-time-${m.message_id}`}
                    className="text-[9px] tracking-wide text-white/35 px-1"
                  >
                    {formatDmTimestamp(m.created_at)}
                  </span>
                  {hasReactions && (
                    <div className="flex flex-wrap gap-1" data-testid={`dm-reactions-${m.message_id}`}>
                      {Object.entries(reactions).map(([emoji, ids]) => {
                        const mine = ids.includes(user?.user_id);
                        return (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => react(m, emoji)}
                            data-testid={`dm-reaction-${m.message_id}-${emoji}`}
                            className={`text-[11px] px-1.5 py-0.5 rounded-full border ${
                              mine
                                ? "bg-[#14b8a6]/25 border-[#14b8a6] text-white"
                                : "bg-black/40 border-white/20 text-white/80 hover:border-white/40"
                            }`}
                          >
                            {emoji} <span className="text-[10px] opacity-70">{ids.length}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {reactingFor === m.message_id && (
                    <div
                      data-testid={`dm-reaction-picker-${m.message_id}`}
                      className="flex gap-0.5 bg-black/85 border border-white/20 rounded-full px-1.5 py-1 shadow-xl"
                    >
                      {REACTIONS.map((e) => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => react(m, e)}
                          data-testid={`dm-react-${m.message_id}-${e}`}
                          className="w-7 h-7 rounded-full hover:bg-white/10 text-base"
                          title={`React ${e}`}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {open ? (
          <form
            onSubmit={submit}
            className="border-t border-white/10 p-3 flex gap-2 shrink-0 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]"
            data-testid="dm-form"
          >
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="GG?"
              maxLength={1000}
              data-testid="dm-input"
              className="flex-1 bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-3 py-2 text-sm text-white"
            />
            <button
              type="submit"
              disabled={sending || !draft.trim()}
              data-testid="dm-send-btn"
              className="px-4 py-2 rounded-2xl text-xs btn-neon disabled:opacity-40"
            >
              SEND
            </button>
          </form>
        ) : (
          <p data-testid="dm-disabled" className="border-t border-white/10 p-4 text-xs text-center text-white/60">
            {policy === "friends" && !areFriends ? (
              <>
                <i className="fas fa-user-shield mr-1 text-[#14b8a6]" />
                {target.name} only accepts DMs from friends. Send a 🤝 friend request first.
              </>
            ) : (
              <>
                <i className="fas fa-ban mr-1 text-[#14b8a6]" />
                {target.name} has DMs turned off.
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

export default DmModal;
